import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Mail, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { useBusinessAuth } from '@/context/BusinessAuthContext';

const LOGO_WORDMARK = "/brand/disqover-wordmark-dark.png";

export default function PartnerLoginPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading, subscription, business, requestMagicLink } = useBusinessAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [debugToken, setDebugToken] = useState(null);
  const [emailDelivered, setEmailDelivered] = useState(false);

  // Redirect authenticated users
  useEffect(() => {
    if (!authLoading && user) {
      if (subscription?.status === 'active' || business) {
        navigate('/portal');
      } else {
        navigate('/partner');
      }
    }
  }, [user, authLoading, subscription, business, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setLoading(true);
    try {
      const result = await requestMagicLink(email);
      setEmailSent(true);
      setEmailDelivered(result.email_sent);
      if (result.debug_token) {
        setDebugToken(result.debug_token);
      }
      toast.success(result.email_sent 
        ? 'Magic link sent!' 
        : 'Check below for your login link.'
      );
    } catch (error) {
      toast.error('Failed to send magic link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="partner-login-page">
      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/partner" 
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Partner Home
        </Link>

        {/* Card */}
        <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
          {emailSent ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-teal-600" />
              </div>
              
              <h1 className="text-2xl font-bold text-slate-900 font-heading mb-2">
                Check your email
              </h1>
              
              <p className="text-slate-600 mb-6">
                We&apos;ve sent a magic link to <strong>{email}</strong>. Click the link to sign in.
              </p>

              {debugToken && (
                <div className="p-4 bg-slate-50 rounded-xl mb-6">
                  <p className="text-xs text-slate-500 mb-2">Testing mode: Click to verify</p>
                  <Link 
                    to={`/partner/verify?token=${debugToken}`}
                    className="text-teal-600 font-medium hover:underline"
                    data-testid="verify-link"
                  >
                    Verify Email →
                  </Link>
                </div>
              )}

              <Button
                variant="outline"
                onClick={() => setEmailSent(false)}
                className="w-full h-12 rounded-full"
              >
                Try different email
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <img src={LOGO_WORDMARK} alt="Disqover" className="h-10 mx-auto mb-6" />
                
                <h1 className="text-2xl font-bold text-slate-900 font-heading mb-2">
                  Sign in to your portal
                </h1>
                
                <p className="text-slate-600">
                  Enter your email and we&apos;ll send you a magic link
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-14 rounded-xl px-5 text-base"
                    data-testid="email-input"
                  />
                </div>

                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 rounded-full bg-teal-600 hover:bg-teal-700 text-base font-medium"
                  data-testid="send-magic-link-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    'Send Magic Link'
                  )}
                </Button>
              </form>

              <p className="text-center text-sm text-slate-500 mt-6">
                Don&apos;t have an account?{' '}
                <Link to="/partner" className="text-teal-600 font-medium hover:underline">
                  Get started
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
